import { createMasterImage, deleteMasterImage } from "../utils/api";
import { MASTER_IMAGE_MAX_BYTES, validateImage } from "../utils/imageValidation";
import { postUpload } from "../utils/uploads";
import { MASK_VERSION } from "./masking";
import type { MasterImage } from "./types";

const PREVIEW_LONG_EDGE = 2500;

const previewOf = (bitmap: ImageBitmap) => {
  const scale = Math.min(1, PREVIEW_LONG_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("Er kon geen voorbeeld van de foto worden gemaakt")),
      "image/jpeg",
      0.9,
    ),
  );
};

export const createMasterImageFromFile = async (
  organization: string,
  series: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<MasterImage> => {
  const contentType = await validateImage(file, MASTER_IMAGE_MAX_BYTES);
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;
  let preview: Blob;
  try {
    preview = await previewOf(bitmap);
  } finally {
    bitmap.close();
  }

  const { masterImage, uploads } = await createMasterImage(organization, series, {
    contentType,
    previewContentType: "image/jpeg",
    width,
    height,
    maskVersion: MASK_VERSION,
  });
  try {
    await postUpload(uploads.preview, preview);
    await postUpload(uploads.image, file, onProgress);
  } catch (e) {
    await deleteMasterImage(organization, series, masterImage._id).catch(() => undefined);
    throw e;
  }
  return masterImage;
};
